/**
 * _shared.tsx
 * Shared types, constants, helper components, and the SchemaPanel renderer
 * used by both ApiReferenceTab and SchemaReferenceTab.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { companyApi } from '../../services/api/companyApi';
import PayloadExecutor from './PayloadExecutor';
import { SECTION_SAMPLES } from './schemaSamples';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import TrainIcon from '@mui/icons-material/Train';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WaterIcon from '@mui/icons-material/Water';
import DirectionsBoatFilledIcon from '@mui/icons-material/DirectionsBoatFilled';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FieldDef {
    name: string;
    type: string;
    required: boolean;
    default?: string;
    allowedValues?: string[];
    format?: string;
    description: string;
    notes?: string;
}

export interface SectionDef {
    id: string;
    title: string;
    subtitle: string;
    icon?: React.ReactNode;
    color: string;
    fields: FieldDef[];
}

// ─── Shared constants ─────────────────────────────────────────────────────────

export const TRUCK_TYPES_V32 = [
    'R03D', 'R07D', 'R18D', 'R26D', 'R36D', 'R44D',
    'R03B', 'R07B', 'R18B', 'R26B', 'R36B', 'R44B',
    'R03E', 'R07E', 'R18E', 'R26E', 'R36E', 'R44E',
    'R03V', 'R07V', 'R18V', 'R26V', 'R36V', 'R44V',
    'R03O', 'R07O', 'R18O', 'R26O', 'R36O', 'R44O',
    'R36L', 'R44L', 'R26H', 'R36H', 'R44H',
];

export const TRUCK_TYPES_DESCRIPTIVE = ['automatic', '3.5T', '7T', '13-18T', '22-36T', '40T'];

export const CALC_PRE_POST = ['0', '1', '2', '3'];

export const ADDR_FIELDS: FieldDef[] = [
    { name: 'PostalCode', type: 'string', required: true, description: 'Postal / ZIP code of the address. Used for geocoding to determine the exact position.' },
    { name: 'TwoLetterCountry', type: 'string', required: true, description: 'ISO 3166-1 alpha-2 two-letter country code (e.g. DE, FR, US, GB).' },
    { name: 'Country', type: 'string', required: false, description: 'Full country name. Optional; TwoLetterCountry is preferred for unambiguous identification.' },
    { name: 'City', type: 'string', required: false, description: 'City or locality name. Helps refine geocoding when postal codes span multiple towns.' },
    { name: 'Street', type: 'string', required: false, description: 'Street address including house number.' },
    { name: 'UnLoCode', type: 'string', required: false, description: 'UN/LOCODE for this location — alternative to a postal address for ports and transport hubs.' },
];

export const COORD_FIELDS: FieldDef[] = [
    { name: 'Lat', type: 'decimal', required: true, format: '-180 to 180', description: 'Geographic latitude.' },
    { name: 'Long', type: 'decimal', required: true, format: '-180 to 180', description: 'Geographic longitude.' },
    { name: 'PostalCode', type: 'string', required: false, description: 'Optional postal code to accompany coordinates.' },
    { name: 'TwoLetterCountry', type: 'string', required: false, description: 'Optional ISO 3166-1 alpha-2 country code to accompany coordinates.' },
    { name: 'Country', type: 'string', required: false, description: 'Optional full country name.' },
    { name: 'City', type: 'string', required: false, description: 'Optional city name.' },
    { name: 'Street', type: 'string', required: false, description: 'Optional street.' },
];

export const AIR_ADDR_FIELDS: FieldDef[] = [
    { name: 'AirPortCode', type: 'string', required: false, description: 'IATA 3-letter airport code (e.g. FRA, LHR, JFK) or a full address string (e.g. "New York, USA"). If a 3-letter IATA code is provided, it is matched directly. If anything else is provided, it is treated as an address and geocoded to find the nearest airport.', notes: 'Provide EITHER AirPortCode (IATA code or address) OR both Coordinates (Lat + Long). Mutually exclusive.' },
    { name: 'Coordinates → Lat', type: 'decimal', required: false, format: '-180 to 180', description: 'Latitude for airport lookup. The nearest airport to these coordinates is used.', notes: 'Required if AirPortCode is omitted. Must be paired with Long.' },
    { name: 'Coordinates → Long', type: 'decimal', required: false, format: '-180 to 180', description: 'Longitude for airport lookup.', notes: 'Required if AirPortCode is omitted. Must be paired with Lat.' },
];

export const SEA_ADDR_FIELDS: FieldDef[] = [
    { name: 'SeaPortCode', type: 'string', required: false, description: 'UN/LOCODE seaport code (e.g. DEHAM, NLRTM, USNYC) or a full address string (e.g. "Hamburg, Germany"). If a 5-letter UN/LOCODE is provided, it is matched directly. If anything else is provided, it is treated as an address and geocoded to find the nearest seaport.', notes: 'Provide EITHER SeaPortCode (UN/LOCODE or address) OR both Coordinates (Lat + Long). Mutually exclusive.' },
    { name: 'Coordinates → Lat', type: 'decimal', required: false, format: '-180 to 180', description: 'Latitude for seaport lookup. The nearest seaport is resolved automatically.', notes: 'Required if SeaPortCode is omitted. Must be paired with Long.' },
    { name: 'Coordinates → Long', type: 'decimal', required: false, format: '-180 to 180', description: 'Longitude for seaport lookup.', notes: 'Required if SeaPortCode is omitted. Must be paired with Lat.' },
];

export const SHIPMENT_COMMON_FIELDS: FieldDef[] = [
    { name: 'Id (attribute)', type: 'string', required: false, description: 'Correlation handle only: echoed back verbatim as the Id attribute of the matching response Shipment, on successful results and on errors alike, so you can match each answer to the shipment you sent. It is never stored and never affects the calculation. Not the shipment reference — for the stored business key, use Reference.', notes: 'Attribute on <Shipment> only — there is no <Id> child element, and sending one is rejected. Values need not be unique. Omit it and the response carries an empty Id.' },
    { name: 'CustomerReference', type: 'string', required: false, default: 'Main', description: 'A descriptive grouping label stored with the shipment and shown on reports and in analytics. Has no effect on the calculation, on replacement or on quota, and is not returned in the response.', notes: 'Accepted as an element or as a CustomerReference attribute on <Shipment>.' },
    { name: 'Reference', type: 'string', required: false, description: 'Your own shipment number — the stored business key CarbonCare identifies the shipment by. Sending the same Reference again for the same account supersedes the earlier calculation (the previous record is retired and a new version written), and each distinct Reference counts once against your unique-shipment allowance however often it is recalculated. Not returned in the response — use the Id attribute to correlate responses.', notes: 'Optional but strongly recommended: if omitted, a synthetic AUTO-<timestamp> reference is generated, so the shipment can never be corrected or de-duplicated later and is not counted as a unique shipment. Accepted as an element or as a Reference attribute on <Shipment>.' },
    { name: 'DateOfShipment', type: 'dateTime', required: false, format: 'YYYY-MM-DDThh:mm:ss', description: 'Date and time of the shipment. ISO 8601 format. Used for temporal emission factor lookups. An empty tag is accepted and treated as absent; when absent, the time of the request is used.' },
    { name: 'Commodity', type: 'string', required: false, description: 'Type of goods/commodity being shipped. Can affect emission factor selection.' },
    { name: 'Handling', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'Whether handling operations (loading/unloading at terminals) should be included in emission calculation.' },
    { name: 'Volume', type: 'decimal', required: false, description: 'Volume of the shipment, used with VolumeUnit.' },
    { name: 'VolumeUnit', type: 'string', required: false, description: 'Unit for Volume. Recorded with the shipment but not read by the calculation engine; any string is accepted. M3 (cubic metres) and CM3 (cubic centimetres) are the conventional values.' },
    { name: 'Weight', type: 'decimal', required: true, description: 'Gross weight in kg. Primary input for emission calculation across all transport modes.' },
    { name: 'WeightUnit', type: 'string', required: false, description: 'Unit for Weight. Recorded with the shipment but not read by the calculation engine, which always interprets Weight as kilograms; any string is accepted. kg is the conventional value.' },
    { name: 'Quote', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'When true, treated as a quote/estimate and may bypass certain strict validation rules.' },
    { name: 'Cooling', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'Indicates temperature-controlled (refrigerated) transport. Affects emission factors.' },
    { name: 'Handling', type: 'boolean', required: false, allowedValues: ['true', 'false'], default: 'false', description: 'Indicates the shipment includes additional handling operations (e.g. loading/unloading). When true, applies additional emission factors for handling activities.' },
];

// ─── Version metadata ─────────────────────────────────────────────────────────

export const VERSION_META: Record<string, { label: string; xsdName: string; summary: string; badge: string }> = {
    '3.2': { label: 'v3.2 (Latest)', xsdName: 'CarbonCareApiRequest 3.2.xsd', badge: 'Latest', summary: 'Adds AirType (ABA/AFA), Rail Highway mode (RAILRH), R-code truck/vessel types, IWW named codes, and new Ferry codes.' },
    '3.0': { label: 'v3.0', xsdName: 'CarbonCareApiRequest 3.0.xsd', badge: 'Stable', summary: 'Adds ropax ferry types and introduces TruckType on Ferry leg.' },
};

export const BADGE_COLOR: Record<string, string> = { Latest: '#10b981', Stable: '#3b82f6', Legacy: '#f59e0b' };

// ─── Shared section builders ──────────────────────────────────────────────────

/**
 * The Sea leg section. Verbatim-identical across all versions except for the
 * From/To field descriptions (v3.2 is slightly more verbose), which are
 * parameterized here. Defaults match the v2.8/v3.0 wording.
 */
export function seaSection(
    fromDescription = 'Origin seaport — SeaPortCode or Coordinates.',
    toDescription = 'Destination seaport.',
): SectionDef {
    return {
        id: 'sea', title: 'Leg → Sea', icon: <DirectionsBoatIcon />, color: '#06b6d4',
        subtitle: 'Ocean freight leg.',
        fields: [
            { name: 'From', type: 'SeaAddressType', required: true, description: fromDescription },
            { name: 'To', type: 'SeaAddressType', required: true, description: toDescription },
        ],
    };
}

/**
 * The Shipment section. Verbatim-identical between v2.8 and v3.0. (v3.2 has its
 * own variant that maps Cooling to a default, so it does not use this helper.)
 */
export function shipmentSection(): SectionDef {
    return {
        id: 'shipment', title: 'Shipment', color: '#10b981',
        subtitle: 'A single shipment. Up to 99 per request, up to 10 Legs each.',
        fields: SHIPMENT_COMMON_FIELDS,
    };
}

/**
 * The four trailing address-type reference sections (address, coordinates,
 * airAddress, seaAddress). Verbatim-identical across all versions except for the
 * coordinates subtitle (v3.2 is slightly more verbose), which is parameterized.
 * Default matches the v2.8/v3.0 wording.
 */
export function tailAddressSections(
    coordinatesSubtitle = 'Alternative to postal address.',
): SectionDef[] {
    return [
        { id: 'address', title: 'AddressType (Standard)', color: '#64748b', subtitle: 'Used by Truck, Rail, IWW, and Ferry From/To.', fields: ADDR_FIELDS },
        { id: 'coordinates', title: 'Coordinates (within AddressType)', color: '#78716c', subtitle: coordinatesSubtitle, fields: COORD_FIELDS },
        { id: 'airAddress', title: 'AirAddressType', color: '#3b82f6', subtitle: 'Used exclusively for Air leg From/To.', fields: AIR_ADDR_FIELDS },
        { id: 'seaAddress', title: 'SeaAddressType', color: '#06b6d4', subtitle: 'Used exclusively for Sea leg From/To.', fields: SEA_ADDR_FIELDS },
    ];
}

export function buildSchemaV32(): SectionDef[] {
    return [
        {
            id: 'root', title: 'Root Element — CarbonCareApi', color: '#6366f1',
            subtitle: 'Top-level wrapper for the entire v3.2 request. Note: the root element name changed from v3.0 (CarbonCareRequestApi) back to CarbonCareApi.',
            fields: [
                { name: 'Version (attribute)', type: 'string (fixed)', required: true, default: '3.2', allowedValues: ['3.2'], description: 'Schema version. Must match the XSD file name version in use.', notes: 'Fixed to "3.2". Do not change.' },
            ],
        },
        {
            id: 'request', title: 'Request', color: '#0ea5e9',
            subtitle: 'Contains the API key and one or more Shipments (up to 99).',
            fields: [
                { name: 'ApiKey', type: 'string', required: true, description: 'Your unique CarbonCare authentication key.' },
            ],
        },
        {
            id: 'shipment', title: 'Shipment', color: '#10b981',
            subtitle: 'A single shipment. Up to 99 per request. Contains Legs (up to 10) for multi-modal routing. v3.2 adds Cooling default=false and uses R-code truck types.',
            fields: [
                ...SHIPMENT_COMMON_FIELDS,
            ].map(f => f.name === 'Cooling' ? { ...f, default: 'false', notes: 'New in v3.2: explicit default of false in schema.' } : f),
        },
        {
            id: 'air', title: 'Leg → Air', icon: <FlightIcon />, color: '#3b82f6',
            subtitle: 'Air freight leg. v3.2 replaces ACType with AirType (ABA/AFA methodology enum).',
            fields: [
                { name: 'From', type: 'AirAddressType', required: true, description: 'Origin airport. Use AirPortCode (IATA) or Coordinates.' },
                { name: 'To', type: 'AirAddressType', required: true, description: 'Destination airport. Use AirPortCode or Coordinates.' },
                { name: 'IsFreighter', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'true = dedicated freighter aircraft, false = belly cargo on passenger flight.' },
                { name: 'AirType', type: 'string (enum)', required: true, default: 'ABA', allowedValues: ['ABA', 'AFA'], description: 'Emission calculation methodology. ABA = Actual Based on Aircraft, AFA = Average Freight Aircraft. Defaults to ABA.', notes: 'New in v3.2. Replaces ACType from v2.8/v3.0.' },
            ],
        },
        seaSection('Origin seaport — SeaPortCode (UN/LOCODE) or Coordinates.', 'Destination seaport — SeaPortCode or Coordinates.'),
        {
            id: 'rail', title: 'Leg → Rail', icon: <TrainIcon />, color: '#f59e0b',
            subtitle: 'Rail freight leg. v3.2 adds Type (RAIL/RAILRH) and TruckType for Rail Highway mode — absent in v2.8 and v3.0.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address for the rail leg.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address for the rail leg.' },
                { name: 'Type', type: 'string (enum)', required: true, default: 'RAIL', allowedValues: ['RAIL', 'RAILRH'], description: 'RAIL = standard rail. RAILRH = Rail Highway (road-on-rail): a truck drives onto a train wagon.', notes: 'New in v3.2. Defaults to RAIL.' },
                { name: 'TruckType', type: 'string (enum)', required: false, default: 'R18D', allowedValues: TRUCK_TYPES_V32, description: 'Only used when Type = RAILRH. Specifies the truck loaded on the train. Defaults to R18D.', notes: 'New in v3.2. Format: R[tonnage][fuel]. Suffixes: D=Diesel, B=Biodiesel (B100), E=Electric (BEV), V=HVO (100%), O=HVO32 (32% blend), L=LNG (R36/R44 only), H=Hydrogen fuel cell (R26/R36/R44 only).' },
            ],
        },
        {
            id: 'truck', title: 'Leg → Truck', icon: <LocalShippingIcon />, color: '#ef4444',
            subtitle: 'Road freight leg. v3.2 switches from descriptive weight labels to precise R-code vehicle identifiers.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address.' },
                { name: 'Type', type: 'string (enum)', required: false, allowedValues: TRUCK_TYPES_V32, description: 'Specific truck vehicle type. If omitted: R18D for distance > 200 km, R44D for ≤ 200 km.', notes: 'v3.2 uses R-codes. Format: R[tonnage][fuel]. Fuel suffixes: D=Diesel, B=Biodiesel (B100), E=Electric (BEV), V=HVO (100%), O=HVO32 (32% blend), L=LNG (R36/R44 only), H=Hydrogen fuel cell (R26/R36/R44 only).' },
                { name: 'LoadFactor', type: 'integer (enum)', required: false, default: '80', allowedValues: ['0', '20', '40', '60', '80', '100'], description: 'Truck load %. v3.2 restricts to specific steps: 0, 20, 40, 60, 80, 100. Default 80%.', notes: 'v2.8/v3.0 accepted any value 0–100; v3.2 restricts to these 6 values.' },
                { name: 'FuelType', type: 'string (enum)', required: false, default: 'Diesel', allowedValues: ['Diesel', 'B100', 'BioDiesel', 'Electric', 'Hydrogen', 'HVO', 'HVO32', 'LNG'], description: 'Explicit fuel type override. Can be used independently or alongside the R-code Type field. Defaults to Diesel.', notes: 'When both FuelType and an R-code Type are provided, FuelType takes precedence for emission factor selection. Hydrogen is only valid for R26+ trucks. LNG is only valid for R36+ trucks.' },
            ],
        },
        {
            id: 'iww', title: 'Leg → IWW (Inland Waterways)', icon: <WaterIcon />, color: '#8b5cf6',
            subtitle: 'Inland waterway freight. v3.2 changes IWW Type from a numeric index (0–11 in v3.0) to descriptive named codes.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin on the inland waterway network.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination on the inland waterway network.' },
                { name: 'Type', type: 'string (enum)', required: true, default: 'IM135D', allowedValues: ['IM40D', 'IM55D', 'IM80D', 'IM110D', 'IM135D', 'IC185D', 'IP2D', 'IP5D', 'IP6D', 'ITVD', 'ICVD', 'ICVCD'], description: 'Vessel type code. Defaults to IM135D if not provided.', notes: 'IM = Inland Motor vessel, IC = Inland Convoy, IP = Inland Pusher, ITV = Tank Vessel, ICV = Container Vessel.' },
            ],
        },
        {
            id: 'ferry', title: 'Leg → Ferry', icon: <DirectionsBoatFilledIcon />, color: '#14b8a6',
            subtitle: 'Short-sea ferry leg. v3.2 uses 4-letter codes: FRO = Ro-Ro, FRX = Ro-Pax, S/L = small/large, D = diesel. Adds TruckType with R-codes.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin port/address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination port/address.' },
                { name: 'Type', type: 'string (enum)', required: true, default: 'FROSD', allowedValues: ['FROSD', 'FROLD', 'FRXSD', 'FRXLD'], description: 'Ferry service type. Defaults to FROSD.', notes: 'FROSD = Ro-Ro ferry, small, diesel; FROLD = Ro-Ro ferry, large, diesel; FRXSD = Ro-Pax ferry, small, diesel; FRXLD = Ro-Pax ferry, large, diesel.' },
                { name: 'TruckType', type: 'string (enum)', required: false, default: 'R18D', allowedValues: TRUCK_TYPES_V32, description: 'Truck type transported on the ferry. Defaults to R18D.', notes: 'Same R-codes as the Truck leg.' },
            ],
        },
        ...tailAddressSections('Alternative to postal address — precise geographic point.'),
    ];
}

export function buildSchemaV28Base(): SectionDef[] {
    return [
        {
            id: 'root', title: 'Root Element — CarbonCareApi', color: '#6366f1',
            subtitle: 'Top-level wrapper for v2.8. Also includes a Response element definition in the schema (for documentation of the API output format).',
            fields: [
                { name: 'Version (attribute)', type: 'string (fixed)', required: true, default: '2.8', allowedValues: ['2.8'], description: 'Schema version.', notes: 'Fixed to "2.8".' },
            ],
        },
        {
            id: 'request', title: 'Request', color: '#0ea5e9',
            subtitle: 'Contains ApiKey and Shipments.',
            fields: [{ name: 'ApiKey', type: 'string', required: true, description: 'Your unique CarbonCare authentication key.' }],
        },
        shipmentSection(),
        {
            id: 'air', title: 'Leg → Air', icon: <FlightIcon />, color: '#3b82f6',
            subtitle: 'Air freight leg. v2.8 supports ACType (no AirType).',
            fields: [
                { name: 'From', type: 'AirAddressType', required: true, description: 'Origin airport — AirPortCode (IATA) or Coordinates.' },
                { name: 'To', type: 'AirAddressType', required: true, description: 'Destination airport.' },
                { name: 'IsFreighter', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'true = dedicated freighter, false = belly cargo.' },
                { name: 'ACType', type: 'string', required: false, description: 'Aircraft type code (IATA aircraft designator, e.g. B744, A333). Allows more precise emission factor selection.', notes: 'Available in v2.8 and v3.0. Removed and replaced by AirType in v3.2.' },
            ],
        },
        seaSection(),
        {
            id: 'rail', title: 'Leg → Rail', icon: <TrainIcon />, color: '#f59e0b',
            subtitle: 'Rail freight leg. v2.8 only supports From and To (same as v3.0). Rail Highway mode added in v3.2.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address.' },
            ],
        },
        {
            id: 'truck', title: 'Leg → Truck', icon: <LocalShippingIcon />, color: '#ef4444',
            subtitle: 'Road freight leg. v2.8 uses descriptive weight labels and a free-range LoadFactor. Also supports CalculationMode.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address.' },
                { name: 'Type', type: 'string (enum)', required: false, allowedValues: TRUCK_TYPES_DESCRIPTIVE, description: 'Truck weight class. If not specified, selected based on the shipment weight.', notes: 'Descriptive labels in v2.8/v3.0. Replaced by R-codes in v3.2.' },
                { name: 'LoadFactor', type: 'integer', required: false, default: '80', format: '0–100', description: 'Truck load as a percentage, any integer 0–100. Default is 80.' },
                { name: 'CalculationMode', type: 'string (enum)', required: false, default: 'GreatCircleDistance', allowedValues: ['GreatCircleDistance', 'RoadDistance'], description: 'Distance calculation method. GreatCircleDistance = straight-line (great circle). RoadDistance = actual road routing distance.', notes: 'Only present in v2.8. Removed in v3.0 and v3.2.' },
            ],
        },
        {
            id: 'iww', title: 'Leg → IWW (Inland Waterways)', icon: <WaterIcon />, color: '#8b5cf6',
            subtitle: 'Inland waterway. v2.8 uses a numeric integer index 0–13 for vessel type.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin on the inland waterway network.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination on the inland waterway network.' },
                { name: 'Type', type: 'integer', required: true, format: '0–13', description: 'Numeric index for vessel type. v2.8 supports 0–13 (wider range than v3.0 which supports 0–11).', notes: 'Changed to named string codes in v3.2.' },
            ],
        },
        {
            id: 'ferry', title: 'Leg → Ferry', icon: <DirectionsBoatFilledIcon />, color: '#14b8a6',
            subtitle: 'Short-sea or RoRo ferry. v2.8 uses simplified large/small ferry types only.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin port/address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination port/address.' },
                { name: 'Type', type: 'string (enum)', required: true, allowedValues: ['large', 'small'], description: 'Ferry size. large or small.', notes: 'Simplified two values in v2.8. v3.0 adds roro/ropax variants. v3.2 uses FROSD/FROLD/… codes.' },
            ],
        },
        ...tailAddressSections(),
    ];
}

export function buildSchemaV30(): SectionDef[] {
    return [
        {
            id: 'root', title: 'Root Element — CarbonCareRequestApi', color: '#6366f1',
            subtitle: 'Top-level wrapper for v3.0. Note: root element name is CarbonCareRequestApi (different from v2.8 and v3.2).',
            fields: [
                { name: 'Version (attribute)', type: 'string (fixed)', required: true, default: '3.0', allowedValues: ['3.0'], description: 'Schema version. Must match the XSD file name version.', notes: 'Fixed to "3.0".' },
            ],
        },
        {
            id: 'request', title: 'Request', color: '#0ea5e9',
            subtitle: 'Contains ApiKey and Shipments (up to 99).',
            fields: [{ name: 'ApiKey', type: 'string', required: true, description: 'Your unique CarbonCare authentication key.' }],
        },
        shipmentSection(),
        {
            id: 'air', title: 'Leg → Air', icon: <FlightIcon />, color: '#3b82f6',
            subtitle: 'Air freight leg. v3.0 retains ACType (removed in v3.2).',
            fields: [
                { name: 'From', type: 'AirAddressType', required: true, description: 'Origin airport — AirPortCode (IATA) or Coordinates.' },
                { name: 'To', type: 'AirAddressType', required: true, description: 'Destination airport.' },
                { name: 'IsFreighter', type: 'boolean', required: false, allowedValues: ['true', 'false'], description: 'true = dedicated freighter, false = belly cargo on passenger flight.' },
                { name: 'ACType', type: 'string', required: false, description: 'Aircraft type code (IATA aircraft type designator, e.g. B744, A333). Used for more precise emission factors.', notes: 'Present in v2.8 and v3.0; replaced by AirType in v3.2.' },
            ],
        },
        seaSection(),
        {
            id: 'rail', title: 'Leg → Rail', icon: <TrainIcon />, color: '#f59e0b',
            subtitle: 'Rail freight leg. v3.0 only supports From and To — no Type or TruckType (those are added in v3.2).',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address for the rail leg.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address for the rail leg.' },
            ],
        },
        {
            id: 'truck', title: 'Leg → Truck', icon: <LocalShippingIcon />, color: '#ef4444',
            subtitle: 'Road freight leg. v3.0 uses descriptive weight labels and free-range LoadFactor (0–100 integer).',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination address.' },
                { name: 'Type', type: 'string (enum)', required: false, allowedValues: TRUCK_TYPES_DESCRIPTIVE, description: 'Truck weight class. If not specified, selected based on shipment weight.', notes: 'Uses descriptive labels in v2.8/v3.0. Replaced by R-codes in v3.2.' },
                { name: 'LoadFactor', type: 'integer', required: false, default: '80', format: '0–100', description: 'Truck load percentage, any integer from 0 to 100. Default is 80.', notes: 'Free-range in v2.8/v3.0. v3.2 restricts to fixed steps (0,20,40,60,80,100).' },
            ],
        },
        {
            id: 'iww', title: 'Leg → IWW (Inland Waterways)', icon: <WaterIcon />, color: '#8b5cf6',
            subtitle: 'Inland waterway freight. v3.0 uses a numeric index 0–11 for vessel type (changed to named codes in v3.2).',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin on the inland waterway network.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination on the inland waterway network.' },
                { name: 'Type', type: 'integer', required: true, format: '0–11', description: 'Numeric index selecting the vessel type. 0–11 in v3.0. Changed to named string codes in v3.2.', notes: 'v2.8 allows 0–13, v3.0 allows 0–11. Refer to CarbonCare documentation for the index-to-vessel mapping.' },
            ],
        },
        {
            id: 'ferry', title: 'Leg → Ferry', icon: <DirectionsBoatFilledIcon />, color: '#14b8a6',
            subtitle: 'Short-sea or RoRo ferry leg. v3.0 uses roro_large/small/ropax_large/small type codes and adds TruckType.',
            fields: [
                { name: 'From', type: 'AddressType', required: true, description: 'Origin port/address.' },
                { name: 'To', type: 'AddressType', required: true, description: 'Destination port/address.' },
                { name: 'Type', type: 'string (enum)', required: true, allowedValues: ['roro_large', 'roro_small', 'ropax_large', 'ropax_small'], description: 'Ferry vessel type. roro = roll-on/roll-off, ropax = roll-on/roll-off + passenger.', notes: 'v2.8 uses large/small only. v3.0 adds ropax variants. v3.2 replaces with FROSD/FROLD/… codes.' },
                { name: 'TruckType', type: 'string (enum)', required: false, allowedValues: TRUCK_TYPES_DESCRIPTIVE, description: 'Truck type transported on the ferry. If not specified, selected by weight.', notes: 'Uses descriptive labels in v3.0. Uses R-codes in v3.2.' },
            ],
        },
        ...tailAddressSections(),
    ];
}

// ─── Helper Components ────────────────────────────────────────────────────────

export const RequiredBadge: React.FC<{ required: boolean }> = ({ required }) => (
    <Chip
        label={required ? 'Required' : 'Optional'}
        size="small"
        sx={{
            fontWeight: 700, fontSize: '0.68rem',
            bgcolor: required ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
            color: required ? '#ef4444' : '#10b981',
            border: `1px solid ${required ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
        }}
    />
);

export const AllowedValuesBadges: React.FC<{ values: string[] }> = ({ values }) => {
    const MAX_INLINE = 8;
    const show = values.slice(0, MAX_INLINE);
    const more = values.length - MAX_INLINE;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {show.map(v => (
                <Chip key={v} label={v} size="small" variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22 }} />
            ))}
            {more > 0 && (
                <Tooltip title={values.slice(MAX_INLINE).join(', ')} arrow
                    componentsProps={{ tooltip: { sx: { fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 400 } } }}>
                    <Chip label={`+${more} more`} size="small"
                        sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22, cursor: 'pointer', bgcolor: 'action.hover' }} />
                </Tooltip>
            )}
        </Box>
    );
};

// ─── Simple 3-column field reference table ────────────────────────────────────
// A Field / Type / Description table used by the Analytics API reference for both
// the response-envelope table and the ShipmentRecord-fields table. The two only
// differ in their data and in two font sizes, parameterized below.

export interface FieldTableRow {
    name: string;
    type: string;
    description: string;
}

export interface FieldTableProps {
    rows: FieldTableRow[];
    /** font-size for the field-name <code> (default '1rem' — browser default). */
    nameFontSize?: string;
    /** font-size for the type <code> (default '0.8rem'). */
    typeFontSize?: string;
}

export const FieldTable: React.FC<FieldTableProps> = ({ rows, nameFontSize, typeFontSize = '0.8rem' }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
            <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map(f => (
                    <TableRow key={f.name} hover>
                        <TableCell>
                            <code style={{ fontWeight: 600, color: '#2e7d32', ...(nameFontSize ? { fontSize: nameFontSize } : {}) }}>{f.name}</code>
                        </TableCell>
                        <TableCell>
                            <code style={{ fontSize: typeFontSize, color: '#555' }}>{f.type}</code>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{f.description}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

// ─── useSchemaState hook ──────────────────────────────────────────────────────

export interface SchemaState {
    selectedVersion: string;
    setSelectedVersion: (v: string) => void;
    search: string;
    setSearch: (v: string) => void;
    expanded: string | false;
    setExpanded: (v: string | false) => void;
    exampleOpen: Record<string, boolean>;
    toggleExample: (id: string) => void;
    apiKey: string;
    filteredSections: SectionDef[];
    matchCount: number;
}

// Module-level predicate so the search filter doesn't nest functions too deeply (S2004)
function fieldMatchesQuery(f: FieldDef, q: string): boolean {
    return (
        f.name.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q) ||
        (f.allowedValues || []).some(v => v.toLowerCase().includes(q)) ||
        f.type.toLowerCase().includes(q)
    );
}

export function useSchemaState(schemas: Record<string, SectionDef[]>): SchemaState {
    const { user } = useAuthStore();
    const [selectedVersion, setSelectedVersion] = useState('3.2');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<string | false>('shipment');
    const [exampleOpen, setExampleOpen] = useState<Record<string, boolean>>({});
    const [apiKey, setApiKey] = useState('');

    React.useEffect(() => {
        if (user?.companyId) {
            companyApi.getById(user.companyId)
                .then((c: any) => setApiKey(c.apiKey || c.api_key || ''))
                .catch(() => { });
        }
    }, [user?.companyId]);

    const toggleExample = useCallback((id: string) => {
        setExampleOpen(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const sections = schemas[selectedVersion] ?? [];

    const filteredSections = useMemo(() => {
        if (!search.trim()) return sections;
        const q = search.toLowerCase();
        return sections.map(s => ({
            ...s,
            fields: s.fields.filter(f => fieldMatchesQuery(f, q)),
        })).filter(s => s.fields.length > 0 || s.title.toLowerCase().includes(q));
    }, [search, sections]);

    const matchCount = useMemo(() =>
        filteredSections.reduce((acc, s) => acc + s.fields.length, 0), [filteredSections]);

    return {
        selectedVersion, setSelectedVersion,
        search, setSearch,
        expanded, setExpanded,
        exampleOpen, toggleExample,
        apiKey,
        filteredSections, matchCount,
    };
}

// ─── SchemaPanel ──────────────────────────────────────────────────────────────
// The full schema reference UI (header, version selector, banner, search, accordions, footer).
// Used by both ApiReferenceTab (as its built-in schema view) and SchemaReferenceTab.

export interface SchemaPanelProps {
    state: SchemaState;
    /** Optional outer wrapper sx — SchemaReferenceTab adds maxWidth/mx:auto */
    wrapperSx?: object;
}

export const SchemaPanel: React.FC<SchemaPanelProps> = ({ state, wrapperSx }) => {
    const {
        selectedVersion, setSelectedVersion,
        search, setSearch,
        expanded, setExpanded,
        exampleOpen, toggleExample,
        apiKey,
        filteredSections, matchCount,
    } = state;

    const meta = VERSION_META[selectedVersion];

    return (
        <Box sx={wrapperSx}>
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <SchemaOutlinedIcon sx={{ color: 'primary.main' }} />
                        <Typography variant="h5" fontWeight={700}>Schema Reference</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        Field-level documentation derived from the official XSD files. Select a version to view its schema.
                    </Typography>
                </Box>

                {/* Version Selector */}
                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Schema Version</InputLabel>
                    <Select
                        value={selectedVersion}
                        label="Schema Version"
                        onChange={e => { setSelectedVersion(e.target.value); setSearch(''); setExpanded('shipment'); }}
                    >
                        {Object.entries(VERSION_META).map(([v, m]) => (
                            <MenuItem key={v} value={v}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {m.label}
                                    <Chip label={m.badge} size="small"
                                        sx={{ fontSize: '0.65rem', height: 18, bgcolor: `${BADGE_COLOR[m.badge]}20`, color: BADGE_COLOR[m.badge], fontWeight: 700 }} />
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Version Summary Banner */}
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            {meta.xsdName}
                            <Chip label={meta.badge} size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 18, bgcolor: `${BADGE_COLOR[meta.badge]}20`, color: BADGE_COLOR[meta.badge], fontWeight: 700 }} />
                        </Typography>
                        <Typography variant="body2">{meta.summary}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Document structure: <code style={{ fontSize: '0.78rem' }}>CarbonCareApi[@Version] → Request → ApiKey + Shipments → Shipment[@Id] → Legs → Leg → Air | Sea | Rail | Truck | Iww | Ferry</code>
                        </Typography>
                    </Box>
                </Box>
            </Alert>

            {/* Search */}
            <TextField
                fullWidth placeholder="Search fields, types, allowed values…"
                value={search} onChange={e => setSearch(e.target.value)}
                size="small" sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
                    endAdornment: search ? <InputAdornment position="end"><Typography variant="caption" color="text.secondary">{matchCount} fields</Typography></InputAdornment> : undefined,
                }}
            />

            {/* Sections */}
            {filteredSections.map(section => (
                <Accordion key={section.id}
                    expanded={expanded === section.id || !!search}
                    onChange={(_, open) => !search && setExpanded(open ? section.id : false)}
                    elevation={0}
                    sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '10px !important', '&:before': { display: 'none' }, overflow: 'hidden' }}
                >
                    <AccordionSummary expandIcon={search ? null : <ExpandMoreIcon />}
                        sx={{ bgcolor: `${section.color}12`, borderBottom: (expanded === section.id || !!search) ? '1px solid' : 'none', borderColor: 'divider', minHeight: 56 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {section.icon && <Box sx={{ color: section.color, display: 'flex' }}>{section.icon}</Box>}
                            <Box>
                                <Typography fontWeight={700} sx={{ color: section.color, fontSize: '0.95rem' }}>{section.title}</Typography>
                                <Typography variant="caption" color="text.secondary">{section.subtitle}</Typography>
                            </Box>
                            <Chip label={`${section.fields.length} field${section.fields.length === 1 ? '' : 's'}`} size="small"
                                sx={{ ml: 1, fontSize: '0.7rem', bgcolor: `${section.color}20`, color: section.color, fontWeight: 600 }} />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer component={Paper} elevation={0}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                                        {['Field', 'Type', 'Required', 'Default', 'Description & Allowed Values'].map(h => (
                                            <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, width: (() => {
                                                if (h === 'Field') return '18%';
                                                if (h === 'Type') return '12%';
                                                if (h === 'Required') return '10%';
                                                if (h === 'Default') return '10%';
                                                return undefined;
                                            })() }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {section.fields.map((field, idx) => (
                                        <TableRow key={field.name}
                                            sx={{ '&:last-child td': { border: 0 }, bgcolor: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)', '&:hover': { bgcolor: `${section.color}08` } }}>
                                            <TableCell>
                                                <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: section.color, bgcolor: `${section.color}12`, px: 0.8, py: 0.3, borderRadius: 1, display: 'inline-block', wordBreak: 'break-word' }}>
                                                    {field.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{field.type}</Typography>
                                                {field.format && <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.68rem' }}>{field.format}</Typography>}
                                            </TableCell>
                                            <TableCell><RequiredBadge required={field.required} /></TableCell>
                                            <TableCell>
                                                {field.default
                                                    ? <Chip label={field.default} size="small" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', height: 22, bgcolor: 'grey.100' }} />
                                                    : <Typography variant="caption" color="text.disabled">—</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.5 }}>{field.description}</Typography>
                                                {field.allowedValues && field.allowedValues.length > 0 && (
                                                    <Box sx={{ mt: 0.75 }}>
                                                        <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>Allowed values</Typography>
                                                        <AllowedValuesBadges values={field.allowedValues} />
                                                    </Box>
                                                )}
                                                {field.notes && (
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.75, color: 'text.secondary', fontStyle: 'italic', fontSize: '0.75rem' }}>
                                                        ℹ️ {field.notes}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Example & Try It Out panel */}
                        {SECTION_SAMPLES[selectedVersion]?.[section.id] && (
                            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box
                                    onClick={() => toggleExample(section.id)}
                                    sx={{
                                        px: 2.5, py: 1.25,
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        cursor: 'pointer',
                                        bgcolor: exampleOpen[section.id] ? `${section.color}10` : 'rgba(0,0,0,0.02)',
                                        '&:hover': { bgcolor: `${section.color}12` },
                                        transition: 'background-color 0.2s',
                                    }}
                                >
                                    <BoltIcon sx={{ fontSize: 16, color: section.color }} />
                                    <Typography variant="body2" fontWeight={700} sx={{ color: section.color, fontSize: '0.82rem' }}>
                                        Example Payload & Try It Out
                                    </Typography>
                                    <Chip
                                        label={exampleOpen[section.id] ? 'Collapse' : 'Expand'}
                                        size="small"
                                        sx={{ ml: 'auto', fontSize: '0.68rem', height: 20, bgcolor: `${section.color}20`, color: section.color, fontWeight: 600, cursor: 'pointer' }}
                                    />
                                </Box>
                                {exampleOpen[section.id] && (
                                    <Box sx={{ p: 2, bgcolor: '#fafafa', borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                                            A complete, runnable API payload for the <strong>{section.title}</strong> section.
                                            Edit the payload below and hit <strong>Execute Request</strong> to test it live.
                                        </Typography>
                                        <PayloadExecutor
                                            samples={SECTION_SAMPLES[selectedVersion][section.id]}
                                            apiKey={apiKey}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}

            {filteredSections.length === 0 && (
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography>No fields match your search for this version.</Typography>
                </Box>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                    <strong>Sources:</strong> <code>CarbonCareApiRequest 3.2.xsd</code> · <code>CarbonCareApiRequest 3.0.xsd</code> · <code>CarbonCareApi 2.8.xsd</code> — All field constraints derived directly from the official XSD schemas. Additional versions will be added as XSD files are provided.
                </Typography>
            </Box>
        </Box>
    );
};
